import {
  BillingCurrencyEnum,
  type ContractorPaymentAPI,
  type ContractorPaymentQuoteAPI,
  FeeType,
  PaymentMethodAPI,
} from '@omnipresentgroup/types';

export const DEFAULT_PAYMENT_QUOTE: ContractorPaymentQuoteAPI = {
  quote_id: '12345',
  expires_at: '2024-02-07T10:51:18.338Z',
  invoices_breakdown: [
    {
      base_amount: {
        amount: '43.21',
        currency: BillingCurrencyEnum.GBP,
      },
      converted_amount: {
        amount: '12.34',
        currency: BillingCurrencyEnum.EUR,
      },
      fees: [
        {
          amount: {
            amount: '0',
            currency: BillingCurrencyEnum.EUR,
          },
          type: FeeType.MANAGEMENT,
          month: '2023-04-01',
        },
        {
          amount: {
            amount: '0',
            currency: BillingCurrencyEnum.EUR,
          },
          type: FeeType.LIABILITY_SHIELD,
          month: '2023-04-01',
        },
      ],
      transfer_amount: {
        amount: '56.78',
        currency: BillingCurrencyEnum.EUR,
      },
      fx: {
        conversion: {
          from_currency: BillingCurrencyEnum.GBP,
          to_currency: BillingCurrencyEnum.EUR,
          effective_rate: '11.2200',
        },
        markup_rate: '0.025',
      },
      vat_rate: '0.2',
      country: {
        code: 'GB',
        name: 'United Kingdom',
        flag: '🇬🇧',
      },
      invoice_id: '9e0fefc5-d7b1-4adc-b1ec-8a714c8f78c4',
      invoice_ref: 'April 2023 invoice (includes all expenses)',
      contractor: {
        id: 'b551a5a1-2641-463f-96a9-1254783a2a48',
        name: 'Test',
      },
    },
  ],
  quote_summary: {
    base_amount: {
      amount: '43.21',
      currency: BillingCurrencyEnum.GBP,
    },
    converted_amount: {
      amount: '12.34',
      currency: BillingCurrencyEnum.EUR,
    },
    fees: [
      {
        amount: {
          amount: '0',
          currency: BillingCurrencyEnum.EUR,
        },
        type: FeeType.MANAGEMENT,
      },
      {
        amount: {
          amount: '0',
          currency: BillingCurrencyEnum.EUR,
        },
        type: FeeType.LIABILITY_SHIELD,
      },
    ],
    transfer_amount: {
      amount: '56.78',
      currency: BillingCurrencyEnum.EUR,
    },
    fx: {
      conversion: {
        from_currency: BillingCurrencyEnum.GBP,
        to_currency: BillingCurrencyEnum.EUR,
        effective_rate: '11.2200',
      },
      markup_rate: '0.025',
    },
    vat_rate: '0.2',
    country: {
      code: 'GB',
      name: 'United Kingdom',
      flag: '🇬🇧',
    },
  },
  payment_type: PaymentMethodAPI.MANUAL_BANK_TRANSFER,
};

export const DEFAULT_PAYMENT: ContractorPaymentAPI = {
  id: '608f471a-191a-48e0-8dd4-78219e85a52f',
  created_at: '2024-03-20T07:32:32.733Z',
  transfer_details: {
    iban_bank_account: {
      owner_name: 'Nameacccountgoeshere',
      owner_address: {
        address_line_1: 'Kinkerstraat 183',
        postal_code: '1080AY',
        city: 'Noord-Holland',
        country: 'Amsterdam',
      },
      bank_account_type: 'IBAN',
      iban: 'NL00 INGB 000 000 00',
      swift_bic: 'BGISDNF626',
    },
    wire_reference: 'FUDIF834HG428JJ29',
  },
  transfer_details_available_until: '2024-03-20T07:32:32.733Z',
  funds_received_at: '2024-03-20T07:32:32.733Z',
  paid_out_at: '2024-03-20T07:32:32.733Z',
  quote: DEFAULT_PAYMENT_QUOTE,
};

export const generatePayment = (
  override: Partial<ContractorPaymentAPI> = {},
) => ({
  ...DEFAULT_PAYMENT,
  ...override,
});

export const GB_PAYMENT: ContractorPaymentAPI = generatePayment({
  id: '608f471a-191a-48e0-8dd4-78219e85ahy5',
  transfer_details: {
    iban_bank_account: {
      owner_name: 'Nameacccountgoeshere',
      owner_address: {
        address_line_1: 'Kinkerstraat 183',
        postal_code: '1080AY',
        city: 'Noord-Holland',
        country: 'Amsterdam',
      },
      bank_account_type: 'IBAN',
      iban: 'NL00 INGB 000 000 00',
      swift_bic: 'BGISDNF626',
    },
    gb_bank_account: {
      owner_name: 'Nameacccountgoeshere',
      owner_address: {
        address_line_1: 'Kinkerstraat 183',
        postal_code: '1080AY',
        city: 'Noord-Holland',
        country: 'Amsterdam',
      },
      bank_account_type: 'GB',
      account_number: '12345678',
      sort_code: '12-34-56',
    },
    wire_reference: 'FUDIF834HG428JJ29',
  },
});

export const generatePaymentQuote = (
  override: Partial<ContractorPaymentQuoteAPI> = {},
) => ({
  ...DEFAULT_PAYMENT_QUOTE,
  ...override,
});

export const PAYMENT_QUOTE_WITH_BREAKDOWN: ContractorPaymentQuoteAPI =
  generatePaymentQuote({
    invoices_breakdown: [
      {
        base_amount: {
          amount: '11.21',
          currency: BillingCurrencyEnum.GBP,
        },
        converted_amount: {
          amount: '3.34',
          currency: BillingCurrencyEnum.EUR,
        },
        fees: [
          {
            amount: {
              amount: '0',
              currency: BillingCurrencyEnum.EUR,
            },
            type: FeeType.MANAGEMENT,
            month: '2023-04-01',
          },
          {
            amount: {
              amount: '0',
              currency: BillingCurrencyEnum.EUR,
            },
            type: FeeType.LIABILITY_SHIELD,
            month: '2023-04-01',
          },
        ],
        transfer_amount: {
          amount: '98.7',
          currency: BillingCurrencyEnum.EUR,
        },
        fx: {
          conversion: {
            from_currency: BillingCurrencyEnum.GBP,
            to_currency: BillingCurrencyEnum.EUR,
            effective_rate: '11.3000',
          },
          markup_rate: '0.075',
        },
        vat_rate: '0.2',
        country: {
          code: 'GB',
          name: 'United Kingdom',
          flag: '🇬🇧',
        },
        invoice_id: '9e0fefc5-d7b1-4adc-b1ec-8a714c8f78c4',
        invoice_ref: 'April 2023 invoice (includes all expenses)',
        contractor: {
          id: 'd00f66dc-d616-4b8b-9a0f-6281d20fee52',
          name: 'John Doe',
        },
      },
      {
        base_amount: {
          amount: '08.21',
          currency: BillingCurrencyEnum.GBP,
        },
        converted_amount: {
          amount: '33.34',
          currency: BillingCurrencyEnum.EUR,
        },
        fees: [
          {
            amount: {
              amount: '0',
              currency: BillingCurrencyEnum.EUR,
            },
            type: FeeType.MANAGEMENT,
            month: '2022-04-01',
          },
          {
            amount: {
              amount: '0',
              currency: BillingCurrencyEnum.EUR,
            },
            type: FeeType.LIABILITY_SHIELD,
            month: '2022-04-01',
          },
        ],
        transfer_amount: {
          amount: '100.7',
          currency: BillingCurrencyEnum.EUR,
        },
        fx: {
          conversion: {
            from_currency: BillingCurrencyEnum.GBP,
            to_currency: BillingCurrencyEnum.EUR,
            effective_rate: '43.3000',
          },
          markup_rate: '0.0',
        },
        vat_rate: '0.09',
        country: {
          code: 'GB',
          name: 'United Kingdom',
          flag: '🇬🇧',
        },
        invoice_id: 'd0bf48c6-3172-484c-89e0-4f053a0852a5',
        invoice_ref: 'April 2022 Invoice',
        contractor: {
          id: 'b551a5a1-2641-463f-96a9-1254783a2a48',
          name: 'Tomas Meyer',
        },
      },
    ],
  });

export const PAYMENT_WITH_MULTIPLE_BREAKDOWNS: ContractorPaymentAPI =
  generatePayment({
    quote: PAYMENT_QUOTE_WITH_BREAKDOWN,
  });
